import {useCallback, useEffect, useRef, useState} from 'react';
import {Canvas} from 'fabric';
import {captureCanvas, restoreCanvas, Snapshot} from '../utils/canvas-snapshot';

const MAX_STACK_SIZE = 50;
const MERGE_WINDOW_MS = 300;

interface UseUndoRedoResult {
    /** Record the current canvas state. Call AFTER each mutation. */
    recordSnapshot: () => void;
    /** Set the initial baseline snapshot after elements load. */
    initSnapshot: () => void;
    /** Undo the last action. */
    undo: () => void;
    /** Redo the last undone action. */
    redo: () => void;
    canUndo: boolean;
    canRedo: boolean;
    /** Ref that is true while a restore is in progress — use to skip recording. */
    isRestoring: React.MutableRefObject<boolean>;
}

export function useUndoRedo(
    canvas: Canvas | null,
    showSeatsAroundTable: boolean,
    onAfterRestore?: () => void,
    /**
     * Reports whether the canvas holds edits: false when a baseline is taken, true on each mutation.
     * This hook is the only place that can tell a user edit from the programmatic `canvas.add()` calls
     * that populate the floorplan on load, which is why the unsaved-changes signal originates here.
     */
    onDirtyChange?: (dirty: boolean) => void,
): UseUndoRedoResult {
    const undoStack = useRef<Snapshot[]>([]);
    const redoStack = useRef<Snapshot[]>([]);
    const currentSnapshot = useRef<Snapshot | null>(null);
    const isRestoring = useRef(false);
    const lastRecordTime = useRef(0);

    // Revision counter to trigger re-renders for canUndo/canRedo
    const [, setRevision] = useState(0);
    const bump = useCallback(() => setRevision(r => r + 1), []);

    // held in a ref so it never changes initSnapshot's identity — that would re-run the caller's
    // element-loading effect, which lists initSnapshot as a dependency, on every render
    const onDirtyChangeRef = useRef(onDirtyChange);
    useEffect(() => {
        onDirtyChangeRef.current = onDirtyChange;
    }, [onDirtyChange]);

    const initSnapshot = useCallback(() => {
        if (!canvas) return;
        currentSnapshot.current = captureCanvas(canvas);
        undoStack.current = [];
        redoStack.current = [];
        onDirtyChangeRef.current?.(false);
        bump();
    }, [canvas, bump]);

    const recordSnapshot = useCallback(() => {
        if (!canvas || isRestoring.current || !currentSnapshot.current) return;

        const now = Date.now();
        const snapshot = captureCanvas(canvas);

        if (now - lastRecordTime.current > MERGE_WINDOW_MS) {
            // New action after pause: push old state to undo stack
            undoStack.current.push(currentSnapshot.current);
            if (undoStack.current.length > MAX_STACK_SIZE) {
                undoStack.current.shift();
            }
            redoStack.current = [];
        }

        // Always update current snapshot (merges rapid changes)
        currentSnapshot.current = snapshot;
        lastRecordTime.current = now;
        onDirtyChangeRef.current?.(true);
        bump();
    }, [canvas, bump]);

    const undo = useCallback(() => {
        if (!canvas || undoStack.current.length === 0 || !currentSnapshot.current) return;

        isRestoring.current = true;

        // Push current state to redo
        redoStack.current.push(currentSnapshot.current);

        // Pop previous state
        const prev = undoStack.current.pop()!;
        currentSnapshot.current = prev;

        restoreCanvas(canvas, prev, showSeatsAroundTable);
        onAfterRestore?.();

        isRestoring.current = false;
        bump();
    }, [canvas, showSeatsAroundTable, onAfterRestore, bump]);

    const redo = useCallback(() => {
        if (!canvas || redoStack.current.length === 0 || !currentSnapshot.current) return;

        isRestoring.current = true;

        // Push current state to undo
        undoStack.current.push(currentSnapshot.current);

        // Pop next state
        const next = redoStack.current.pop()!;
        currentSnapshot.current = next;

        restoreCanvas(canvas, next, showSeatsAroundTable);
        onAfterRestore?.();

        isRestoring.current = false;
        bump();
    }, [canvas, showSeatsAroundTable, onAfterRestore, bump]);

    return {
        recordSnapshot,
        initSnapshot,
        undo,
        redo,
        canUndo: undoStack.current.length > 0,
        canRedo: redoStack.current.length > 0,
        isRestoring,
    };
}
